import {localizeUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {PublicRegistrationLanguage} from "./types";

const insufficientRoomsPattern = /^Insufficient rooms for (\d{4}-\d{2}-\d{2}) \(remaining (\d+)\)\.$/i;

const formatSubmitErrorDate = (
    isoDate: string,
    language: PublicRegistrationLanguage
): string | null => {
    const date = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat(language === "hr" ? "hr-HR" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC"
    }).format(date);
};

const fillSubmitErrorTemplate = (
    template: string,
    values: Record<string, string>
): string => Object.entries(values)
    .reduce((message, [key, value]) => message.replaceAll(`{${key}}`, value), template);

export const localizeSubmitErrorMessage = (
    value: string,
    labels: Record<string, string>,
    language: PublicRegistrationLanguage = "en"
): string => {
    const message = value.trim();

    if (message === labels.submitError ||
        message === labels.requiredFieldsError ||
        message === labels.invalidValuesError) {
        return message;
    }

    if (message === "At least one registration item is required.") {
        return labels.atLeastOneItem;
    }

    if (message === "Participation fee is not configured.") {
        return labels.participationFeeMissing;
    }

    if (message === "No payment methods are configured for participation.") {
        return labels.missingParticipationPaymentMethods;
    }

    if (message === "No payment methods are configured for accommodation.") {
        return labels.missingAccommodationPaymentMethods;
    }

    if (message === "GDPR/privacy consent is required.") {
        return labels.privacyConsentRequired;
    }

    if (message === "Participant type proof file is required.") {
        return labels.participantTypeProofRequired;
    }

    if (message === "Participant type proof file requires individual registration.") {
        return labels.bulkProofNotSupported;
    }

    if (message === "LinkedIn URL format is invalid.") {
        return labels.invalidLinkedInUrl;
    }

    if (message === "A registration already exists for this email address and event.") {
        return labels.duplicateRegistrationEmail;
    }

    if (message === "Public registration is unavailable because no active form is configured.") {
        return labels.registrationUnavailableFormInactive;
    }

    if (message === "Public registration is unavailable because the event is archived.") {
        return labels.registrationUnavailableArchived;
    }

    if (message === "Public registration is unavailable because the event is completed.") {
        return labels.registrationUnavailableCompleted;
    }

    if (message === "Public registration is unavailable because the event has ended.") {
        return labels.registrationUnavailableEnded;
    }

    const insufficientRoomsMatch = message.match(insufficientRoomsPattern);
    if (insufficientRoomsMatch) {
        const formattedDate = formatSubmitErrorDate(insufficientRoomsMatch[1], language);
        const remainingRooms = Number.parseInt(insufficientRoomsMatch[2], 10);
        if (formattedDate && Number.isFinite(remainingRooms)) {
            return fillSubmitErrorTemplate(
                remainingRooms === 0 ? labels.noRoomsRemaining : labels.roomsRemaining,
                {
                    date: formattedDate,
                    remaining: String(remainingRooms)
                }
            );
        }
    }

    const localizedTechnicalMessage = localizeUserFacingErrorMessage(message, key => key, {
        fallback: labels.submitError,
        requiredFields: labels.requiredFieldsError,
        invalidValues: labels.invalidValuesError
    });

    return localizedTechnicalMessage === message
        ? labels.submitError
        : localizedTechnicalMessage;
};
